import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconPlus } from "@tabler/icons-react";
import { createMultimediaRequest } from "@services/multimedia/multimediaApi";
import InlineError from "@components/forms/InlineError";
import {
  appendTourMultimedia,
  removeTourMultimedia,
  setTourBanner,
  setTourSnapshot,
  setTourMultimediaAudioTitle,
} from "@store/slices/tourDraftSlice";
import { inferMediaType, resolveMediaUrl } from "@utils/media/mediaUrl";
import MediaPicker from "@components/media/MediaPicker";

// Normalizes multimedia entities from backend into picker-compatible shape.
function normalizeMultimediaItem(item) {
  const rawUrl = item?.url || item?.mmd_url || "";
  return {
    id: item?.id || item?.mmd_id || "",
    url: resolveMediaUrl(rawUrl),
    mediaType:
      item?.mediaType || item?.mmd_media_type || inferMediaType(rawUrl),
    mimeType: item?.mimeType || item?.mmd_mime_type || "",
  };
}

// Handles multiple possible backend payload shapes.
function extractMultimediaList(body) {
  if (Array.isArray(body?.multimediaList))
    return body.multimediaList.map(normalizeMultimediaItem);
  if (Array.isArray(body?.multimedia))
    return body.multimedia.map(normalizeMultimediaItem);
  if (body?.multimedia) return [normalizeMultimediaItem(body.multimedia)];
  return [];
}

// Upload manager for main tour image + tour images + promo media in tour form.
function TourAddMultimedia({
  languages = [],
  activeLanguageIso = "",
  bannerValidationError = "",
  snapshotValidationError = "",
  tourImagesValidationError = "",
  promoVideoValidationError = "",
  promoAudioValidationError = "",
  promoAudioTitlesValidationError = "",
}) {
  const dispatch = useDispatch();
  const multimedia = useSelector((state) => state.tourDraft.multimedia || []);
  const banner = useSelector((state) => state.tourDraft.banner || null);
  const snapshot = useSelector((state) => state.tourDraft.snapshot || null);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [isUploadingSnapshot, setIsUploadingSnapshot] = useState(false);
  const [imagesError, setImagesError] = useState("");
  const [videoError, setVideoError] = useState("");
  const [audioError, setAudioError] = useState("");
  const [bannerError, setBannerError] = useState("");
  const [snapshotError, setSnapshotError] = useState("");
  const matchesMediaKind = (item, kind) => {
    const mediaType = String(item?.mediaType || "").toLowerCase();
    const mimeType = String(item?.mimeType || "").toLowerCase();
    return mediaType === kind || mimeType.startsWith(`${kind}/`);
  };
  const currentLanguage = (languages || []).find((language) => {
    const iso = String(language?.lng_iso || language?.iso || "").toLowerCase();
    return iso === activeLanguageIso;
  });
  const currentLanguageIso =
    activeLanguageIso ||
    String(
      currentLanguage?.lng_iso || currentLanguage?.iso || "",
    ).toLowerCase();
  const currentLanguageName =
    currentLanguage?.lng_name ||
    currentLanguage?.name ||
    currentLanguageIso.toUpperCase();
  const tourImages = (multimedia || []).filter((item) =>
    matchesMediaKind(item, "image"),
  );
  const promoVideo = (multimedia || [])
    .filter(
      (item) =>
        matchesMediaKind(item, "video") &&
        item?.mediaRole === "promoVideo" &&
        item?.languageIso === currentLanguageIso,
    )
    .slice(0, 1);
  const promoAudioAllLanguages = (multimedia || []).filter(
    (item) =>
      matchesMediaKind(item, "audio") && item?.mediaRole === "promoAudio",
  );
  const [draftAudioGroupIds, setDraftAudioGroupIds] = useState([]);
  const audioGroupIds = useMemo(() => {
    const existingGroupIds = promoAudioAllLanguages
      .map((item) =>
        String(item?.audioGroupId || item?.sourceId || item?.id || "").trim(),
      )
      .filter(Boolean);
    return Array.from(new Set([...draftAudioGroupIds, ...existingGroupIds]));
  }, [draftAudioGroupIds, promoAudioAllLanguages]);

  useEffect(() => {
    const existingGroupIds = promoAudioAllLanguages
      .map((item) =>
        String(item?.audioGroupId || item?.sourceId || item?.id || "").trim(),
      )
      .filter(Boolean);
    setDraftAudioGroupIds((current) =>
      Array.from(new Set([...current, ...existingGroupIds])),
    );
  }, [promoAudioAllLanguages]);

  async function uploadMediaFiles(
    files,
    mediaKind,
    setUploading,
    setError,
    extraItem = {},
  ) {
    if (!files.length) return;
    setError("");
    setUploading(true);
    try {
      const payload = files.length === 1 ? { file: files[0] } : { files };
      const response = await createMultimediaRequest(payload);
      let items = extractMultimediaList(response.body);
      items = items.filter((item) => matchesMediaKind(item, mediaKind));
      if (mediaKind === "video") {
        const existingVideos = (multimedia || []).filter(
          (item) =>
            matchesMediaKind(item, "video") &&
            item?.mediaRole === "promoVideo" &&
            item?.languageIso === extraItem?.languageIso,
        );
        if (existingVideos.length) {
          dispatch(removeTourMultimedia(existingVideos[0].id));
        }
        items = items.slice(0, 1);
      }
      if (mediaKind === "audio") {
        items = items.map((item, index) => ({
          ...item,
          audioGroupId:
            extraItem?.audioGroupId ||
            item?.audioGroupId ||
            item?.sourceId ||
            item?.id ||
            `audio-group-${Date.now()}-${index + 1}`,
          audioTitles: {
            [extraItem?.languageIso || ""]: String(
              item?.audioTitles?.[extraItem?.languageIso || ""] || "",
            ),
          },
          sortOrder: Date.now() + index,
        }));
      }
      items = items.map((item) => ({
        ...item,
        sourceId: item?.id || "",
        languageIso: extraItem?.languageIso || "",
        mediaRole: extraItem?.mediaRole || "",
      }));
      if (items.length) dispatch(appendTourMultimedia(items));
    } catch (error) {
      setError(error.message || `Could not upload ${mediaKind}.`);
    } finally {
      setUploading(false);
    }
  }

  // Uploads a dedicated banner image (single file).
  async function uploadBanner(files) {
    if (!files.length) return;
    setBannerError("");
    setIsUploadingBanner(true);
    try {
      const response = await createMultimediaRequest({ file: files[0] });
      // Banner accepts a single created multimedia item.
      const [item] = extractMultimediaList(response.body);
      if (!item?.id) {
        setBannerError("Could not create banner.");
        return;
      }
      dispatch(setTourBanner(item));
    } catch (error) {
      setBannerError(error.message || "Could not upload banner.");
    } finally {
      setIsUploadingBanner(false);
    }
  }

  // Uploads a dedicated snapshot image (single file).
  async function uploadSnapshot(files) {
    if (!files.length) return;
    setSnapshotError("");
    setIsUploadingSnapshot(true);
    try {
      const response = await createMultimediaRequest({ file: files[0] });
      const [item] = extractMultimediaList(response.body);
      if (!item?.id) {
        setSnapshotError("Could not create tour snapshot image.");
        return;
      }
      dispatch(setTourSnapshot(item));
    } catch (error) {
      setSnapshotError(
        error.message || "Could not upload tour snapshot image.",
      );
    } finally {
      setIsUploadingSnapshot(false);
    }
  }

  function handleAddAudioGroup() {
    const nextGroupId = `audio-group-${Date.now()}`;
    setDraftAudioGroupIds((current) =>
      current.includes(nextGroupId) ? current : [...current, nextGroupId],
    );
  }

  function handleRemoveAudio(mediaId, groupId) {
    const groupItems = promoAudioAllLanguages.filter(
      (item) =>
        String(
          item?.audioGroupId || item?.sourceId || item?.id || "",
        ).trim() === String(groupId || "").trim(),
    );
    dispatch(removeTourMultimedia(mediaId));
    if (groupItems.length <= 1) {
      setDraftAudioGroupIds((current) =>
        current.filter((item) => item !== groupId),
      );
    }
  }

  return (
    <>
      {/* Dedicated banner zone. Banner is required and independent from multimedia list. */}
      <MediaPicker
        title="Main Tour Image (Required)"
        titleError={bannerValidationError}
        items={banner?.id ? [banner] : []}
        isUploading={isUploadingBanner}
        uploadError={bannerError}
        accept={{ "image/*": [] }}
        maxFiles={1}
        invalidTypeMessage="Banner must be an image."
        onRejected={(message) => setBannerError(message)}
        onUpload={uploadBanner}
        onRemove={() => dispatch(setTourBanner(null))}
        uploadLabel="Drag main tour image here or click to upload"
        uploadHint="Image only. This file is used as the main tour image."
      />
      {/* Tour images (required). */}
      <MediaPicker
        title="Tour Images (Required)"
        titleError={tourImagesValidationError}
        items={tourImages}
        isUploading={isUploadingImages}
        uploadError={imagesError}
        accept={{ "image/*": [] }}
        maxFiles={10}
        invalidTypeMessage="Only images are allowed in this section."
        onRejected={(message) => setImagesError(message)}
        onUpload={(files) =>
          uploadMediaFiles(files, "image", setIsUploadingImages, setImagesError)
        }
        onRemove={(id) => dispatch(removeTourMultimedia(id))}
        uploadLabel="Drag tour images here or click to upload"
        uploadHint="Images only (at least 1 required)."
      />
      <MediaPicker
        title="Tour Promo Video (Required)"
        titleError={promoVideoValidationError}
        items={promoVideo}
        isUploading={isUploadingVideo}
        uploadError={videoError}
        accept={{ "video/*": [] }}
        maxFiles={1}
        invalidTypeMessage="Only one video is allowed."
        onRejected={(message) => setVideoError(message)}
        onUpload={(files) =>
          uploadMediaFiles(files, "video", setIsUploadingVideo, setVideoError, {
            languageIso: currentLanguageIso,
            mediaRole: "promoVideo",
          })
        }
        onRemove={(id) => dispatch(removeTourMultimedia(id))}
        uploadLabel={`Drag promo video (${currentLanguageName}) here or click to upload`}
        uploadHint="Video only. One promo video is required for each language."
      />
      <div className="tour-add-form-group">
        <div className="tour-audio-groups-head">
          <p className="tour-add-form-label">
            Tour Audio Preview (Required)
            {promoAudioValidationError || promoAudioTitlesValidationError ? (
              <InlineError>
                {promoAudioValidationError || promoAudioTitlesValidationError}
              </InlineError>
            ) : null}
          </p>
          <button
            type="button"
            className="app-icon-btn app-icon-btn--add"
            onClick={handleAddAudioGroup}
            title="Add audio block"
            aria-label="Add audio block"
          >
            <IconPlus size={16} />
          </button>
        </div>
        <div className="tour-audio-groups-list">
          {audioGroupIds.map((groupId, index) => {
            const groupItems = promoAudioAllLanguages.filter(
              (item) =>
                String(
                  item?.audioGroupId || item?.sourceId || item?.id || "",
                ).trim() === String(groupId || "").trim(),
            );
            const currentAudioItem =
              groupItems.find(
                (item) => item?.languageIso === currentLanguageIso,
              ) || null;
            const availableIsos = Array.from(
              new Set(
                groupItems
                  .map((item) =>
                    String(item?.languageIso || "")
                      .toLowerCase()
                      .trim(),
                  )
                  .filter(Boolean),
              ),
            );
            return (
              <div key={groupId} className="tour-audio-group-card">
                <div className="tour-audio-group-card-head">
                  <strong>{`Audio ${index + 1}`}</strong>
                  <small>
                    {availableIsos.length
                      ? `Available: ${availableIsos.join(", ").toUpperCase()}`
                      : "No files uploaded yet"}
                  </small>
                </div>
                <MediaPicker
                  title={`Audio ${index + 1} (${currentLanguageName})`}
                  items={currentAudioItem ? [currentAudioItem] : []}
                  isUploading={isUploadingAudio}
                  uploadError={audioError}
                  accept={{ "audio/*": [] }}
                  maxFiles={1}
                  invalidTypeMessage="Only audio files are allowed."
                  onRejected={(message) => setAudioError(message)}
                  onUpload={(files) =>
                    uploadMediaFiles(
                      files,
                      "audio",
                      setIsUploadingAudio,
                      setAudioError,
                      {
                        languageIso: currentLanguageIso,
                        mediaRole: "promoAudio",
                        audioGroupId: groupId,
                      },
                    )
                  }
                  onRemove={(id) => handleRemoveAudio(id, groupId)}
                  uploadLabel={`Drag promo audio ${index + 1} (${currentLanguageName}) here or click to upload`}
                  uploadHint={`This slot belongs to Audio ${index + 1}. Use the same block in every language.`}
                  renderItemExtra={(item) => (
                    <div className="tour-audio-titles">
                      <span className="tour-audio-titles-label">
                        {`Audio ${index + 1} title - ${currentLanguageName}`}
                      </span>
                      <div className="tour-audio-titles-body">
                        <label className="field-label app-form-field tour-audio-title-row">
                          <input
                            className="field-input"
                            value={
                              item?.audioTitles?.[currentLanguageIso] || ""
                            }
                            placeholder={`Write audio ${index + 1} title (${currentLanguageIso})`}
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                            onChange={(event) =>
                              dispatch(
                                setTourMultimediaAudioTitle({
                                  mediaId: item.id,
                                  iso: currentLanguageIso,
                                  value: event.target.value,
                                }),
                              )
                            }
                          />
                        </label>
                      </div>
                    </div>
                  )}
                />
              </div>
            );
          })}
        </div>
      </div>
      <MediaPicker
        title="Tour Snapshot Image (Required)"
        titleError={snapshotValidationError}
        items={snapshot?.id ? [snapshot] : []}
        isUploading={isUploadingSnapshot}
        uploadError={snapshotError}
        accept={{ "image/*": [] }}
        maxFiles={1}
        invalidTypeMessage="Tour snapshot image must be an image."
        onRejected={(message) => setSnapshotError(message)}
        onUpload={uploadSnapshot}
        onRemove={() => dispatch(setTourSnapshot(null))}
        uploadLabel="Drag tour snapshot image here or click to upload"
        uploadHint="Image only. Maximum 1."
      />
    </>
  );
}

export default TourAddMultimedia;
